/**
 * serviceEditorShared.jsx
 * ─────────────────────────────────────────────────────────
 * Shared helpers used by every Jeyshawn service-detail page:
 *   • ImageUploadField
 *   • ServiceEditModal  (generic: title, paragraphs, list sections, images)
 *   • usePageEditor     (fetch / save / toast hook)
 */
import { useState, useEffect, useRef, useCallback } from "react";

export const BASE_URL   = "https://staging.pteshstudios.com";
export const UPLOAD_URL = `${BASE_URL}/api/upload`;

export const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BASE_URL}${url}`;
};

/* ═══════════════════════════════
   IMAGE UPLOAD FIELD
═══════════════════════════════ */
export function ImageUploadField({ label, currentUrl, onUploaded }) {
  const [preview, setPreview]     = useState(resolveUrl(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveUrl(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res  = await fetch(UPLOAD_URL, { method: "POST", body: formData });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Upload failed");
      onUploaded(json.url);
      setPreview(resolveUrl(json.url));
    } catch (err) {
      setError("Upload failed. Try again.");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview" />
          {uploading && (
            <div className="editor-img-uploading">
              <div className="editor-loading__spinner editor-loading__spinner--sm" />
              <span>Uploading...</span>
            </div>
          )}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload image</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10 MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* ═══════════════════════════════
   GENERIC SERVICE EDIT MODAL
   ─────────────────────────────
   data shape expected:
   {
     bannerImage : string,
     bgImage     : string,   // optional — section background
     title       : string,
     paragraphs  : string[], // ordered intro paragraphs
     sections    : [{ heading: string, items: string[] }]
   }
═══════════════════════════════ */
export function ServiceEditModal({ data, onSave, onClose, saving, pageLabel }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(data)));

  const setPara    = (i, v) => setDraft(d => { const p = [...d.paragraphs]; p[i] = v; return { ...d, paragraphs: p }; });
  const addPara    = ()     => setDraft(d => ({ ...d, paragraphs: [...d.paragraphs, ""] }));
  const removePara = (i)    => setDraft(d => ({ ...d, paragraphs: d.paragraphs.filter((_, idx) => idx !== i) }));

  const setSectionHeading = (si, v)     => setDraft(d => { const s = [...d.sections]; s[si] = { ...s[si], heading: v }; return { ...d, sections: s }; });
  const setSectionItem    = (si, ii, v) => setDraft(d => { const s = [...d.sections]; const items = [...s[si].items]; items[ii] = v; s[si] = { ...s[si], items }; return { ...d, sections: s }; });
  const addSectionItem    = (si)        => setDraft(d => { const s = [...d.sections]; s[si] = { ...s[si], items: [...s[si].items, ""] }; return { ...d, sections: s }; });
  const removeSectionItem = (si, ii)    => setDraft(d => { const s = [...d.sections]; s[si] = { ...s[si], items: s[si].items.filter((_, idx) => idx !== ii) }; return { ...d, sections: s }; });
  const addSection        = ()          => setDraft(d => ({ ...d, sections: [...d.sections, { heading: "New Section", items: [""] }] }));
  const removeSection     = (si)        => setDraft(d => ({ ...d, sections: d.sections.filter((_, idx) => idx !== si) }));

  return (
    <div className="editor-overlay" onClick={onClose}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        <div className="editor-modal__header">
          <h2>Edit — {pageLabel}</h2>
          <button className="editor-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="editor-modal__body">

          {/* PAGE IMAGES */}
          <p className="editor-section__title">Page Images</p>
          <div className="editor-group">
            <ImageUploadField
              label="Banner Image (top of page)"
              currentUrl={draft.bannerImage}
              onUploaded={url => setDraft(d => ({ ...d, bannerImage: url }))}
            />
            {"bgImage" in draft && (
              <ImageUploadField
                label="Section Background Image"
                currentUrl={draft.bgImage}
                onUploaded={url => setDraft(d => ({ ...d, bgImage: url }))}
              />
            )}
          </div>

          {/* MAIN TITLE */}
          <p className="editor-section__title">Main Content</p>
          <div className="editor-group">
            <label>Title / Heading</label>
            <textarea rows={3} value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} />
          </div>

          {/* PARAGRAPHS */}
          <p className="editor-section__title">Paragraphs</p>
          <div className="editor-group">
            <p className="editor-group__label">Intro Paragraphs (displayed in order)</p>
            {draft.paragraphs.map((para, i) => (
              <div key={i} className="editor-value-row">
                <textarea
                  rows={4}
                  value={para}
                  onChange={e => setPara(i, e.target.value)}
                  style={{ flex: 1, padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                />
                <button className="editor-value-row__remove" onClick={() => removePara(i)} title="Remove paragraph">✕</button>
              </div>
            ))}
            <button className="editor-add-btn" onClick={addPara}>+ Add Paragraph</button>
          </div>

          {/* BULLET SECTIONS */}
          {draft.sections.length > 0 && (
            <>
              <p className="editor-section__title">Bullet Sections</p>
              {draft.sections.map((sec, si) => (
                <div key={si} className="editor-group">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <p className="editor-group__label">Section {si + 1}</p>
                    <button className="editor-value-row__remove" onClick={() => removeSection(si)} title="Remove section" style={{ marginBottom: 4 }}>✕</button>
                  </div>
                  <label>Section Heading</label>
                  <input value={sec.heading} onChange={e => setSectionHeading(si, e.target.value)} />
                  <label>List Items</label>
                  {sec.items.map((item, ii) => (
                    <div key={ii} className="editor-value-row">
                      <input value={item} onChange={e => setSectionItem(si, ii, e.target.value)} />
                      <button className="editor-value-row__remove" onClick={() => removeSectionItem(si, ii)} title="Remove item">✕</button>
                    </div>
                  ))}
                  <button className="editor-add-btn" onClick={() => addSectionItem(si)}>+ Add Item</button>
                </div>
              ))}
            </>
          )}
          <button className="editor-add-btn" onClick={addSection} style={{ marginTop: draft.sections.length > 0 ? 0 : 0 }}>
            + Add Bullet Section
          </button>

        </div>

        <div className="editor-modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--orange" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}


export function usePageEditor(apiEndpoint, defaultData) {
  const [pageData, setPageData]   = useState(defaultData);
  const [loading, setLoading]     = useState(true);
  const [fetchErr, setFetchErr]   = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);

  const showToast = useCallback((msg) => {
    setSaveMsg(msg);
    setTimeout(() => setSaveMsg(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setFetchErr(null);

      const res  = await fetch(`${BASE_URL}${apiEndpoint}`);
      const json = await res.json();

      if (json.success) {
        setPageData(json.data);
      } else {
        throw new Error(json.message || "Failed to fetch");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setFetchErr("Could not load page data. Using defaults.");
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (draft) => {
    try {
      setSaving(true);

      const res = await fetch(`${BASE_URL}${apiEndpoint}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });

      const json = await res.json();

      if (!json.success) {
        throw new Error(json.message || "Save failed");
      }

      await fetchData();
      setModalOpen(false);
      showToast("Changes saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      showToast("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return {
    pageData,
    loading,
    fetchErr,
    fetchData,
    modalOpen,
    setModalOpen,
    saving,
    handleSave,
    saveMsg,
  };
}